import { useCopyToClipboard } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { useQuery } from '@tanstack/react-query'
import type { MonthlySale } from 'backend/types'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { SALES_STAGE_MAP } from 'shared'
import { getMemberMonthlySales } from '@/lib'
import { convertPeriodFromMonth, getCurrentPeriod } from '@/lib/period-utils'
import { queryKeys } from '@/lib/queryKeys'

export type MonthlySaleWithPeriod = MonthlySale & { period: string | null }

/**
 * sales_on フィールド（YYYY-MM形式の文字列）をDateTimeに変換
 */
function parseSalesOn(salesOn: MonthlySale['sales_on']): DateTime {
  return DateTime.fromFormat(salesOn, 'yyyy-MM')
}

export function useMemberMonthlySalesWithPeriod() {
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(getCurrentPeriod())
  const [searchText, setSearchText] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.memberMonthlySales,
    queryFn: async () => {
      return getMemberMonthlySales({})
    },
  })

  const salesWithPeriod = useMemo<MonthlySaleWithPeriod[]>(() => {
    if (!data?.sales) return []
    return data.sales.map((sale) => {
      const dt = parseSalesOn(sale.sales_on)
      const yearMonth = dt.isValid ? dt.toFormat('yyyy-MM') : ''
      const period = convertPeriodFromMonth(yearMonth)
      return { ...sale, period }
    })
  }, [data?.sales])

  const availablePeriods = useMemo(() => {
    const currentPeriod = getCurrentPeriod()
    const periodsFromData = salesWithPeriod.map((sale) => sale.period).filter((p): p is string => p !== null)
    const allPeriods = currentPeriod ? [currentPeriod, ...periodsFromData] : periodsFromData
    const uniquePeriods = [...new Set(allPeriods)]
    return uniquePeriods.sort((a, b) => (a > b ? 1 : -1))
  }, [salesWithPeriod])

  const filteredSales = useMemo(() => {
    if (!selectedPeriod) return []

    let filtered = salesWithPeriod.filter((sale) => sale.period === selectedPeriod)

    if (searchText) {
      const lowerSearch = searchText.toLowerCase()
      filtered = filtered.filter((sale) => {
        const searchableFields = [
          sale.opportunity_id,
          sale.sales_id,
          sale.salesforce_customer_name,
          sale.salesforce_project_name,
          sale.salesforce_original_project_name,
        ]
        return searchableFields.some((field) =>
          String(field || '')
            .toLowerCase()
            .includes(lowerSearch),
        )
      })
    }

    return filtered
  }, [salesWithPeriod, selectedPeriod, searchText])

  const headers = [
    'オポチュニティ管理ID',
    '売上情報ID',
    '取引先名',
    'ANOの案件名',
    '売上情報の案件名',
    'SalesStage',
    '失注前SalesStage',
    'オポ金額',
    '売上金額',
    '売上月',
    '売上情報所有者',
  ]

  const extractValues = useCallback((item: MonthlySale) => {
    const salesOnDt = parseSalesOn(item.sales_on)
    return [
      item.opportunity_id || '',
      item.sales_id,
      item.salesforce_customer_name || '',
      item.salesforce_project_name || '',
      item.salesforce_original_project_name || '',
      item.sales_stage_id ? (SALES_STAGE_MAP[item.sales_stage_id]?.text ?? 'ー') : 'ー',
      item.previously_closed_sales_stage_id
        ? (SALES_STAGE_MAP[item.previously_closed_sales_stage_id]?.text ?? 'ー')
        : 'ー',
      item.opportunity_amount || '',
      item.amount,
      salesOnDt.isValid ? salesOnDt.toFormat('yyyy/MM/dd') : '',
      item.sales_information_owner_member_name || '',
    ]
  }, [])

  const copyToClipboard = useCopyToClipboard(headers, extractValues)

  return {
    filteredSales,
    availablePeriods,
    isLoading,
    error,
    selectedPeriod,
    setSelectedPeriod,
    searchText,
    setSearchText,
    copyToClipboard,
  }
}
